import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Search, MapPin, Globe, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDisplayDate } from "@/lib/utils";
import { City, CityFormData, Country, State } from "@/types/geography";
import {
  useConfirmDialog,
  createDeleteConfirm,
  createDeactivateConfirm,
} from "@/components/ui/confirm-dialog";
import DataTablePagination from "@/components/ui/data-table-pagination";
import {
  useCities as useCitiesApi,
  useCity as useCityApi,
  useCreateCity,
  useUpdateCity,
  useDeleteCity,
} from "@/api-services/hooks/useCityRefactored";
import { useStates as useStatesApi } from "@/api-services/hooks/useStateRefactored";
import { useCountries as useCountriesApi } from "@/api-services/hooks/useCountryRefactored";
import {
  buildCityPayload,
  mapCityMasterToCity,
  mapCityToPayload,
  mapStateMasterToState,
  mapCountryMasterToCountry,
  mapStatusToApi,
} from "@/utils/geographyMappers";
import { API_BASE_URL } from '@/api-services/config';
import type { CityMasterDTO } from '@/api-services/hooks/types';

const CitiesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [cities, setCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [selectedStateId, setSelectedStateId] = useState<string>("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  const [formData, setFormData] = useState<CityFormData>({
    name: "",
    countryId: "",
    status: "active",
  });

  // Validation errors state
  const [errors, setErrors] = useState<{
    name?: string;
    countryId?: string;
    stateId?: string;
    status?: string;
  }>({});

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'City name is required';
    } else {
      // Check for unique name (case-insensitive) globally across all countries and states
      const isDuplicateName = cities.some(
        city => city.name.toLowerCase() === formData.name.toLowerCase() &&
        city.id !== editingCity?.id
      );
      if (isDuplicateName) {
        newErrors.name = 'City with this name already exists';
      }
    }

    // Country validation
    if (!formData.countryId) {
      newErrors.countryId = 'Country is required';
    }

    // State validation
    if (!selectedStateId || selectedStateId === "no-state") {
      newErrors.stateId = 'State is required';
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation on input change
  const handleInputChange = (field: keyof CityFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const cityQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);
  const stateQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);
  const countryQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);

  const {
    data: cityResponse,
    loading: citiesLoading,
    error: citiesError,
    refetch: refetchCities,
  } = useCitiesApi(cityQueryParams);

  const {
    data: stateResponse,
    loading: statesLoading,
    error: statesError,
  } = useStatesApi(stateQueryParams);

  const {
    data: countryResponse,
    loading: countriesLoading,
    error: countriesError,
  } = useCountriesApi(countryQueryParams);

  const createCityMutation = useCreateCity();
  const updateCityMutation = useUpdateCity();
  const deleteCityMutation = useDeleteCity();

  // Fetch existing city data when editing to preserve system IP fields
  const { data: existingCityData } = useCityApi(
    editingCity ? Number(editingCity.id) : 0,
    {
      enabled: !!editingCity && !!editingCity.id,
    }
  );

  const actionLoading =
    createCityMutation.loading ||
    updateCityMutation.loading ||
    deleteCityMutation.loading;

  useEffect(() => {
    if (cityResponse?.content) {
      const mapped = cityResponse.content.map(mapCityMasterToCity);
      setCities(mapped);
    } else {
      setCities([]);
    }
  }, [cityResponse]);

  useEffect(() => {
    if (stateResponse?.content) {
      const mapped = stateResponse.content.map(mapStateMasterToState);
      setStates(mapped);
    } else {
      setStates([]);
    }
  }, [stateResponse]);

  useEffect(() => {
    if (countryResponse?.content) {
      const mapped = countryResponse.content.map(mapCountryMasterToCountry);
      setCountries(mapped);
    } else {
      setCountries([]);
    }
  }, [countryResponse]);

  useEffect(() => {
    if (citiesError) {
      toast.error(citiesError.message);
    }
  }, [citiesError]);

  useEffect(() => {
    if (statesError) {
      toast.error(statesError.message);
    }
  }, [statesError]);

  useEffect(() => {
    if (countriesError) {
      toast.error(countriesError.message);
    }
  }, [countriesError]);

  useEffect(() => {
    let filtered = cities;

    if (searchTerm) {
      filtered = filtered.filter((city) =>
        city.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((city) => city.status === statusFilter);
    }

    if (countryFilter !== "all") {
      filtered = filtered.filter((city) => city.countryId === countryFilter);
    }

    if (stateFilter !== "all") {
      filtered = filtered.filter(
        (city) => (city.stateId ?? "no-state") === stateFilter
      );
    }

    setFilteredCities(filtered);
    setCurrentPage(1);
  }, [cities, searchTerm, statusFilter, countryFilter, stateFilter]);

  useEffect(() => {
    setStateFilter("all");
  }, [countryFilter]);

  useEffect(() => {
    if (!formData.countryId) {
      setSelectedStateId("");
      return;
    }

    const hasMatchingState = states.some(
      (state) =>
        state.countryId === formData.countryId && state.id === selectedStateId
    );

    if (!hasMatchingState) {
      // Reset to empty when country changes and current state doesn't belong to new country
      setSelectedStateId("");
    }
  }, [formData.countryId, states, selectedStateId]);

  const totalItems = filteredCities.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCities = filteredCities.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      countryId: "",
      status: "active",
    });
    setErrors({});
    setSelectedStateId(""); // Start with empty state instead of "no-state"
    setEditingCity(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    const normalizedForm: CityFormData = {
      ...formData,
      stateId: selectedStateId || undefined,
    };

    try {
      if (editingCity) {
        const payload = await buildCityPayload(normalizedForm, editingCity.id, existingCityData || undefined);
        await updateCityMutation.mutate({
          id: Number(editingCity.id),
          data: payload,
        });
        toast.success("City updated successfully!");
      } else {
        const payload = await buildCityPayload(normalizedForm);
        await createCityMutation.mutate(payload);
        toast.success("City created successfully!");
      }

      await refetchCities();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to save city";

      // Handle server-side validation errors for duplicates
      if (message.toLowerCase().includes('duplicate') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('unique')) {
        if (message.toLowerCase().includes('name')) {
          setErrors(prev => ({
            ...prev,
            name: 'City with this name already exists'
          }));
        } else {
          // Generic duplicate error
          setErrors(prev => ({
            ...prev,
            name: 'Duplicate city detected'
          }));
        }
      }

      toast.error(message);
    }
  };

  const handleEdit = (city: City) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      countryId: city.countryId,
      status: city.status,
      metadata: city.metadata || {},
    });
    setSelectedStateId(city.stateId || "");
    setIsDialogOpen(true);
  };

  const handleDelete = (city: City) => {
    if (actionLoading) return;

    const cityName = city.name;
    confirm(
      createDeleteConfirm(
        "City",
        async () => {
          try {
            await deleteCityMutation.mutate(Number(city.id));
            toast.success(`${cityName} deleted successfully!`);
            await refetchCities();
          } catch (error) {
            const message =
              error instanceof Error ? error.message : "Failed to delete city";
            toast.error(message);
          }
        },
        `Are you sure you want to delete ${cityName}? This will permanently remove the city and all associated data. This action cannot be undone.`
      )
    );
  };

  const handleStatusToggle = (city: City) => {
    if (actionLoading) return;

    const isActive = city.status === "active";
    const cityName = city.name;

    confirm(
      createDeactivateConfirm("City", isActive, async () => {
        try {
          const newStatus = isActive ? "inactive" : "active";

          // Optimistically update the cache immediately
          queryClient.setQueriesData(
            { queryKey: ['cities'] },
            (oldData: any) => {
              if (!oldData?.content) return oldData;

              const updatedContent = oldData.content.map((item: any) =>
                item.cityId === city.id
                  ? { ...item, isActive: newStatus === "active" }
                  : item
              );

              return {
                ...oldData,
                content: updatedContent,
              };
            }
          );

          // Fetch existing city data to preserve system IP fields
          let existingCityData: CityMasterDTO | undefined;
          try {
            const response = await fetch(`${API_BASE_URL}/city/${city.id}`);
            if (response.ok) {
              const apiResponse = await response.json();
              existingCityData = apiResponse.data;
            }
          } catch (err) {
            // Silently handle fetch error
          }
          const basePayload = await mapCityToPayload(city, existingCityData);
          const payload = {
            ...basePayload,
            status: mapStatusToApi(newStatus),
          };
          await updateCityMutation.mutate({
            id: Number(city.id),
            data: payload,
          });
          toast.success(
            `${cityName} ${
              newStatus === "active" ? "activated" : "deactivated"
            } successfully!`
          );
        } catch (error) {
          // Revert optimistic update on error
          queryClient.setQueriesData(
            { queryKey: ['cities'] },
            (oldData: any) => {
              if (!oldData?.content) return oldData;

              const updatedContent = oldData.content.map((item: any) =>
                item.cityId === city.id
                  ? { ...item, isActive: isActive }
                  : item
              );

              return {
                ...oldData,
                content: updatedContent,
              };
            }
          );
          const message =
            error instanceof Error
              ? error.message
              : "Failed to update city status";
          toast.error(message);
        }
      })
    );
  };

  const getCountryName = (countryId: string) => {
    const country = countries.find((c) => c.id === countryId);
    return country ? country.name : "Unknown";
  };

  const getStateName = (stateId?: string) => {
    if (!stateId) return "N/A";
    const state = states.find((s) => s.id === stateId);
    return state ? state.name : "Unknown";
  };

  const availableStates = formData.countryId
    ? states.filter((state) => state.countryId === formData.countryId)
    : [];

  const statesForFilters =
    countryFilter === "all"
      ? states
      : states.filter((state) => state.countryId === countryFilter);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Cities</h1>
          <p className="text-muted-foreground">
            Manage cities and municipalities
          </p>
        </div>
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            if (!open) resetForm();
            setIsDialogOpen(open);
          }}
        >
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add City
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingCity ? "Edit City" : "Add New City"}
              </DialogTitle>
              <DialogDescription>
                {editingCity
                  ? "Update the city information below."
                  : "Add a new city to the system."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">City Name *</label>
                  <Input
                    key="city-name-input"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.name.trim()) {
                        const isDuplicateName = cities.some(
                          city => city.name.toLowerCase() === formData.name.toLowerCase() &&
                          city.id !== editingCity?.id
                        );
                        if (isDuplicateName) {
                          setErrors(prev => ({ ...prev, name: 'City with this name already exists' }));
                        }
                      }
                    }}
                    placeholder="Los Angeles"
                    className={errors.name ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium">Country *</label>
                  <Select
                    value={formData.countryId}
                    onValueChange={(value) => {
                      handleInputChange('countryId', value);
                    }}
                  >
                    <SelectTrigger
                      disabled={countriesLoading || countries.length === 0}
                      className={errors.countryId ? "border-red-500 focus:ring-red-500" : ""}
                    >
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.id} value={country.id}>
                          {country.name} ({country.code})
                        </SelectItem>
                      ))}
                      {!countriesLoading && countries.length === 0 && (
                        <SelectItem value="no-country" disabled>
                          No countries available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {errors.countryId && (
                    <p className="text-sm text-red-600 mt-1">{errors.countryId}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">State / Province *</label>
                <Select
                  value={selectedStateId}
                  onValueChange={setSelectedStateId}
                >
                  <SelectTrigger
                    disabled={statesLoading || !formData.countryId}
                    className={errors.stateId ? "border-red-500 focus:ring-red-500" : ""}
                  >
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStates.map((state) => (
                      <SelectItem key={state.id} value={state.id}>
                        {state.name}
                      </SelectItem>
                    ))}
                    {!statesLoading && availableStates.length === 0 && (
                      <SelectItem value="no-state" disabled>
                        No states available. Please select a country first.
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.stateId && (
                  <p className="text-sm text-red-600 mt-1">{errors.stateId}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Status *</label>
                <Select
                  value={formData.status}
                  onValueChange={(value: "active" | "inactive") => {
                    handleInputChange('status', value);
                  }}
                >
                  <SelectTrigger className={errors.status ? "border-red-500 focus:ring-red-500" : ""}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-600 mt-1">{errors.status}</p>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading
                    ? "Saving..."
                    : editingCity
                    ? "Update"
                    : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cities</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{cities.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Cities</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {cities.filter((city) => city.status === "active").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive Cities</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {cities.filter((city) => city.status === "inactive").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">States</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(cities.map((city) => city.stateId).filter(Boolean)).size}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Countries</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(cities.map((city) => city.countryId)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search cities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country.id} value={country.id}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                <SelectItem value="no-state">No state</SelectItem>
                {statesForFilters.map((state) => (
                  <SelectItem key={state.id} value={state.id}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cities ({filteredCities.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {citiesLoading && (
            <div className="text-sm text-muted-foreground mb-4">
              Loading cities...
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead>State</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCities.map((city) => (
                <TableRow key={city.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {getCountryName(city.countryId)}
                    </div>
                  </TableCell>
                  <TableCell>{getStateName(city.stateId)}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{city.name}</div>
                      {city.metadata?.timezone && (
                        <div className="text-xs text-muted-foreground">
                          Timezone: {city.metadata.timezone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        city.status === "active" ? "default" : "secondary"
                      }
                      className="cursor-pointer"
                      onClick={() => handleStatusToggle(city)}
                    >
                      {city.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {formatDisplayDate(city.createdAt)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(city)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(city)}
                        disabled={actionLoading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!citiesLoading && filteredCities.length === 0 && (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No cities found</h3>
              <p className="text-muted-foreground">
                {searchTerm ||
                statusFilter !== "all" ||
                countryFilter !== "all" ||
                stateFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Get started by adding your first city"}
              </p>
            </div>
          )}

          {filteredCities.length > 0 && (
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      <ConfirmDialog />
    </div>
  );
};

export default CitiesPage;
